import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProjectIntegration, ProviderType } from '../models/project-integration.model';
import { issueBrowseUrl } from '../shared/issue-links';
import { providerSupportsIssueCreation } from '../shared/provider-meta';
import { ProjectIntegrationService } from './project-integration.service';

/**
 * Wie weit der Abruf der Anbindungen ist. Eine leere Liste allein sagt es nicht: sie
 * steht sowohl fuer „Projekt ohne Anbindung" als auch fuer „noch nicht da" und fuer
 * „Abruf gescheitert" — drei Lagen, aus denen die Oberflaeche Verschiedenes macht.
 */
export type IntegrationLoadState = 'pending' | 'ready' | 'failed';

/**
 * Wohin der Absprung zu einem verknuepften Bug-Vorgang fuehrt.
 *
 * <p>Drei Herkuenfte, eine Antwort: einen soeben angelegten Vorgang verlinkt der Server
 * selbst, und dieser Link gilt. Ein Schluessel, der schon vorher am Schritt stand, kommt
 * ohne Adresse aus dem Runner-Zustand — nennt der Schritt die Anbindung, in der der
 * Vorgang angelegt wurde, wird die Adresse aus ihr gebildet (NEX-1224). Sonst, etwa im
 * Bestand, aus der ersten aktiven Issue-Anbindung des Projekts. Ohne Anbindung bleibt
 * der Absprung aus; der Block, in dem er steht, ist davon nicht betroffen.
 *
 * <p>Bewusst nicht {@code providedIn: 'root'} — die gemerkten Server-Links gehoeren der
 * Seite, die den Vorgang angelegt hat, und sollen mit ihr enden.
 */
@Injectable()
export class StepBugLinkService {
  private readonly integrationService = inject(ProjectIntegrationService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly loaded = signal<ProjectIntegration[]>([]);
  private readonly loadState = signal<IntegrationLoadState>('pending');
  private readonly activeIntegration = computed(() => this.loaded().find((integration) => integration.active) ?? null);
  private readonly serverUrls = signal<Record<string, string>>({});

  /**
   * Alle Anbindungen des Projekts, so wie der Server sie gemeldet hat. Der Runner
   * waehlt daraus das Ziel einer Bug-Anlage (NEX-1205) — geholt werden sie ohnehin
   * schon fuer den Absprung, ein zweiter Abruf waere derselbe Aufruf ein zweites Mal.
   */
  readonly integrations = this.loaded.asReadonly();

  /** Wie weit der Abruf ist — wer daraus eine Auswahl baut, braucht mehr als die Liste. */
  readonly integrationsLoadState = this.loadState.asReadonly();

  /**
   * Die Verbindungen, in denen ein Vorgang ueberhaupt entstehen kann: abgeschaltete
   * scheitern am Server, und ein Anbieter ohne Anlage-Unterstuetzung antwortet mit 501.
   * Beides waere ein Ziel, das nur enttaeuschen kann.
   */
  readonly creationTargets = computed(() =>
    this.loaded().filter(
      (integration) => integration.active && providerSupportsIssueCreation(integration.providerType),
    ),
  );

  /**
   * Ob eine Anlage angeboten werden darf. Die leere Liste allein entscheidet das nicht:
   * vor der Antwort und nach einem gescheiterten Abruf ist sie ebenso leer wie bei einem
   * Projekt ohne Anbindung — nur dort ist „kein Ziel" auch die Wahrheit.
   */
  readonly canCreateIssue = computed(() => this.loadState() === 'ready' && this.creationTargets().length > 0);

  /**
   * Holt alle Anbindungen des Projekts. Fuer den Absprung bleibt ein Fehlschlag ohne
   * Folgen — die Verknuepfung liegt am Schritt, nur ihr Ziel bliebe unbekannt. Gemeldet
   * wird er trotzdem, damit eine Auswahl ihn nicht fuer „keine Anbindung" nimmt.
   */
  load(projectId: string): void {
    this.loadState.set('pending');
    this.integrationService
      .list(projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (integrations) => {
          this.loaded.set(integrations);
          this.loadState.set('ready');
        },
        error: () => this.loadState.set('failed'),
      });
  }

  /** Merkt sich den Link, den der Server zu einem angelegten Vorgang gemeldet hat. */
  remember(stepId: string, url: string): void {
    this.serverUrls.update((urls) => ({ ...urls, [stepId]: url }));
  }

  /**
   * Ziel des Absprungs, {@code null} ohne Schluessel. Die Anbindung des Schritts wird nur
   * in der vom Server geladenen Liste nachgeschlagen (NEX-1191) — auch abgeschaltet, denn
   * der Vorgang liegt weiter dort.
   */
  urlFor(stepId: string, key: string, integrationId?: string | null): string | null {
    if (!key) return null;
    return (
      this.serverUrls()[stepId] ?? this.derivedUrl(key, this.integrationOf(integrationId) ?? this.activeIntegration())
    );
  }

  private derivedUrl(key: string, integration: ProjectIntegration | null): string {
    return issueBrowseUrl(this.provider(integration), key, this.boardUrl(integration));
  }

  private integrationOf(integrationId?: string | null): ProjectIntegration | null {
    if (!integrationId) return null;
    return this.loaded().find((integration) => integration.id === integrationId) ?? null;
  }

  private provider(integration: ProjectIntegration | null): ProviderType {
    return integration?.providerType ?? 'JIRA';
  }

  private boardUrl(integration: ProjectIntegration | null): string | null {
    return integration?.boardUrl ?? null;
  }
}
